// Shared "Labor resources" chip template: a neutral pill holding a dark
// avatar (the acronym) and the units, with a remove control shown on hover.
// With the portfolio's units option off, the chip is the dark acronym bubble
// alone (the "--compact" classes).
// Used by the resourceassignment column of the main project Gantt and the
// Scenario Gantt (spec D5), which share this column config, so the display
// rule and test hooks live in one place.
//
// Does NOT import from "./utils": utils.js imports { NonLaborResourceColumn,
// processDefaultColumns } from "./config", so importing utils.js here (while
// config.js imports this module) would create a load-order cycle. getInitials
// is instead re-implemented locally as initialsOf, mirroring utils.js's
// getInitials.
import i18n from "../../../i18n";
import { escapeHtml } from "../../../utils/escapeHtml";

// Portfolio option (Settings > Gantt options): show each resource's units next
// to its acronym and let a click on the chip edit them. Fed from the portfolio
// details like the display-unit cache; off until the details say otherwise,
// which is the option's default.
let laborUnitsEnabled = false;

export const setLaborUnitsEnabled = (details) => {
  laborUnitsEnabled = details?.laborUnitsEnabled === true;
};

export const isLaborUnitsEnabled = () => laborUnitsEnabled;

const initialsOf = (name) => {
  if (!name) return "";
  const words = name.split(" ");
  const firstLetter = words[0][0];
  const secondLetter = words.length > 1 ? words.slice(-1)[0][0] : "";
  return (firstLetter + secondLetter).toUpperCase();
};

export const formatUnits = (units) => {
  const n = Number(units);
  if (!Number.isFinite(n)) return "0%";
  return `${Number.isInteger(n) ? n : Number(n.toFixed(2))}%`;
};

const LABOR_ENTITY = "TEAMS";

const acronymOf = (resource) => resource.acronym || initialsOf(resource.name);

// The column fits two pills; a third wraps out of sight. The cell tooltip lists
// every labor couple so none is unreachable. HTML (escaped): Bryntum renders
// tooltip output as markup.
export const laborCouplesTooltip = (assignments) =>
  (assignments || [])
    .filter((a) => a?.resource?.entityType === LABOR_ENTITY)
    .map((a) => `${escapeHtml(acronymOf(a.resource))} ${formatUnits(a.units)}`)
    .join(" · ");

// The remove control carries no text, so the chip's textContent stays
// "<ACRONYM><units>%" (just "<ACRONYM>" with the units option off) for whoever
// reads it (e2e helpers, the export).
export const laborChipTpl = (assignment) => {
  const { resource, units, event, eventId } = assignment;
  if (!resource || resource.entityType !== LABOR_ENTITY) return "";

  const initials = escapeHtml(acronymOf(resource));
  const taskId = event?.id ?? eventId ?? "";
  const unitsHtml = laborUnitsEnabled
    ? `<span class="offolio-labor-chip__units" data-testid="gantt-labor-chip-units-${taskId}-${resource.id}">${formatUnits(units)}</span>`
    : "";
  const chipTip = escapeHtml(
    laborUnitsEnabled ? resource.name : `${resource.name} · ${formatUnits(units)}`
  );
  const removeLabel = escapeHtml(
    i18n.t("ganttContext.unitsInlineEdit.remove", { name: resource.name })
  );

  return `
    <div class="offolio-labor-chip${laborUnitsEnabled ? "" : " offolio-labor-chip--compact"}"
         data-testid="gantt-labor-chip-${taskId}-${resource.id}"
         data-resource-id="${resource.id}"
         data-btip="${chipTip}">
      <span class="offolio-labor-chip__acronym" data-testid="gantt-labor-chip-acronym-${taskId}-${resource.id}">${initials}</span>${unitsHtml}<span class="offolio-labor-chip__remove" role="button" aria-label="${removeLabel}" data-btip="${removeLabel}" data-testid="gantt-labor-chip-remove-${taskId}-${resource.id}"></span>
    </div>`;
};

// The dashed "+" closing the chip list: opens the assignment picker.
export const laborAddButtonHtml = (taskId) => {
  const label = escapeHtml(i18n.t("ganttContext.unitsInlineEdit.add"));
  return `<span class="offolio-labor-add" role="button" aria-label="${label}" data-btip="${label}" data-testid="gantt-labor-add-${taskId}"></span>`;
};

// Runs after Bryntum's own chip-view renderer (as the column's `renderer`, it
// is Bryntum's "external" one) and finishes the cell: flags the chip list for
// styling, hides the remove control on a lone chip (a task keeps at least one
// labor resource: the picker's save refuses an empty selection too) and on a
// row the user cannot edit, and ends an editable row's list with the "+".
export const decorateLaborCell = ({ cellElement, record, isExport }, { canAdd } = {}) => {
  if (isExport || !cellElement) return;
  const chipView = cellElement.querySelector(".b-assignment-chipview");
  if (!chipView) return;
  const laborCount = (record?.assignments || []).filter(
    (a) => a?.resource?.entityType === LABOR_ENTITY
  ).length;
  const editable = !!record && !!canAdd?.(record);
  chipView.classList.add("offolio-labor-chipview");
  chipView.classList.toggle("offolio-labor-chipview--single", laborCount <= 1);
  chipView.classList.toggle("offolio-labor-chipview--readonly", !editable);
  // Beside the chip view, not inside it: the chip view clips what does not
  // fit on the line, and the "+" must never be clipped nor cover a chip.
  // Bryntum empties the wrap on every render, so there is no stale "+".
  if (editable) chipView.insertAdjacentHTML("afterend", laborAddButtonHtml(record.id));
};
